import { useEffect, useId, useRef, useState } from "react";
import type { AssignedPracticeAsset, AssetPracticePreview, AssetPracticeTarget, AssetPracticeTask } from "@wmf/shared/assignedAssetPractice";
import { ApiError, get, send } from "./api.js";
import { Drawer } from "./ui/Dialog.js";

export function SetAssetPracticeTask({ target, source, assetTitle, disabled = false, onSaved, onBusyChange }: {
  target: AssetPracticeTarget;
  source: AssignedPracticeAsset;
  assetTitle: string;
  disabled?: boolean;
  onSaved?: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const contextKey = `${target.domain}:${target.learnerId}:${JSON.stringify(source)}`;
  useEffect(() => { setOpen(false); setSaved(false); }, [contextKey]);
  return <>
    <button type="button" className="quiet small" disabled={disabled} aria-label={`Set as practice task: ${assetTitle}`}
      onClick={() => { setSaved(false); setOpen(true); }}>Set as practice task</button>
    {saved && <span className="small" role="status">Practice task set</span>}
    {open && <AssetPracticeForm key={contextKey} target={target} source={source} assetTitle={assetTitle}
      onClose={() => setOpen(false)} onBusyChange={onBusyChange}
      onSaved={() => { setOpen(false); setSaved(true); onSaved?.(); }} />}
  </>;
}

function AssetPracticeForm({ target, source, assetTitle, onClose, onSaved, onBusyChange }: {
  target: AssetPracticeTarget; source: AssignedPracticeAsset; assetTitle: string;
  onClose: () => void; onSaved: () => void; onBusyChange?: (busy: boolean) => void;
}) {
  const id = useId();
  const [preview, setPreview] = useState<AssetPracticePreview | null>(null);
  const [title, setTitle] = useState(assetTitle.slice(0, 200));
  const [instructions, setInstructions] = useState("");
  const [minutes, setMinutes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const requestId = useRef<string | null>(null);
  const submitLock = useRef(false);
  const mounted = useRef(true);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; busyCallback.current?.(false); }; }, []);
  const path = `/assigned-assets/${target.domain}/${encodeURIComponent(target.learnerId)}/practice`;
  const sourceKey = JSON.stringify(source);
  useEffect(() => {
    let current = true;
    setLoading(true); setProblem(null); setPreview(null);
    get<AssetPracticePreview>(path, { source: sourceKey }).then(value => {
      if (!current) return;
      setPreview(value); setTitle(value.task?.title ?? value.assetTitle.slice(0, 200));
      setInstructions(value.task?.instructions ?? ""); setMinutes(value.task?.suggestedMinutes == null ? "" : String(value.task.suggestedMinutes));
    }).catch(cause => { if (current) setProblem(cause instanceof Error ? cause.message : "Could not check this assigned asset."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [path, sourceKey, reload]);
  const numericMinutes = minutes === "" ? null : Number(minutes);
  const minutesValid = numericMinutes === null || (Number.isInteger(numericMinutes) && numericMinutes >= 1 && numericMinutes <= 480);
  async function save() {
    if (submitLock.current || loading || !preview || !title.trim() || !minutesValid) return;
    submitLock.current = true; setBusy(true); setSubmitted(true); setProblem(null); busyCallback.current?.(true);
    try {
      requestId.current ??= practiceRequestId();
      await send<{ task: AssetPracticeTask; created: boolean }>("POST", path, {
        source, title: title.trim(), instructions: instructions.trim() || null,
        suggestedMinutes: numericMinutes, requestId: requestId.current,
      });
      if (mounted.current) onSaved();
    } catch (cause) {
      if (mounted.current) {
        // A definitive refusal can be corrected. An uncertain save must retry the same payload and key.
        if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.status !== 408) {
          requestId.current = null; setSubmitted(false);
        }
        setProblem(cause instanceof Error ? cause.message : "Could not set this practice task.");
      }
    }
    finally { submitLock.current = false; if (mounted.current) { setBusy(false); busyCallback.current?.(false); } }
  }
  return <Drawer title="Set as practice task" eyebrow={assetTitle} onClose={onClose} busy={busy} size="medium"
    description="Set work using this asset. The student can open it from Practice and log their time.">
    {loading && <p role="status">Checking assigned asset…</p>}
    {problem && <p className="error small" role="alert">{problem}</p>}
    {!loading && !preview && <button type="button" className="quiet" onClick={() => setReload(value => value + 1)}>Retry checking asset</button>}
    {preview && <form className="stack" onSubmit={event => { event.preventDefault(); void save(); }}>
      {preview.task && <p className="small muted">This asset already has an unfinished practice task. Saving updates its instructions and time; its practice history is kept.</p>}
      <label htmlFor={`${id}-title`}>Task</label>
      <input id={`${id}-title`} required maxLength={200} value={title} disabled={busy || submitted} onChange={event => setTitle(event.target.value)} />
      <label htmlFor={`${id}-instructions`}>Instructions</label>
      <textarea id={`${id}-instructions`} rows={4} maxLength={2000} placeholder="What should the student work on with this asset?" value={instructions} disabled={busy || submitted} onChange={event => setInstructions(event.target.value)} />
      <label htmlFor={`${id}-minutes`}>Suggested minutes per day (optional)</label>
      <input id={`${id}-minutes`} type="number" min={1} max={480} step={1} inputMode="numeric" value={minutes} disabled={busy || submitted} aria-invalid={!minutesValid} onChange={event => setMinutes(event.target.value)} />
      {!minutesValid && <p className="error small">Enter a whole number from 1 to 480 minutes.</p>}
      <button type="submit" disabled={busy || !title.trim() || !minutesValid}>{busy ? "Setting practice…" : submitted ? "Retry setting practice task" : "Set as practice task"}</button>
    </form>}
  </Drawer>;
}

function practiceRequestId() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 15) | 64; bytes[8] = (bytes[8]! & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
